/**
 * IndividualNraSection — Non-Resident Alien documentation summary.
 *
 * Reworked per Bill's NRA smoke test (2026-06-12) + LOCKED mockup v6
 * (06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html — "W-8BEN / W-9 ceremony stays
 * on SSN-TIN per cohort" host; rendered inside the Profile / SSN-TIN card):
 *
 *   - Renders ONLY when person.citizenshipStatus = 'Non-Resident Alien'
 *   - Plain-banking copy only — no developer/jXchange wording in the UI
 *   - COLLAPSED by default; auto-expands ONLY when the captured NRA documents
 *     disagree with each other (cross-document conflict detection via
 *     detectCrossDocConflicts over person.extra.extracts). Manual expander
 *     always available.
 *   - Fields populate from the document-analyzer extracts when the canonical
 *     person slots are empty (visa class/expiration, I-94 number/expiration,
 *     foreign passport number, issuing country). An I-94 "Admit Until" of
 *     D/S renders as "D/S — duration of status", never null/blank.
 *   - W-8 fields REMOVED (Bill: W-8 is not required for CIP) — country of
 *     tax residency, W-8 form type / last signed / expiration, IRS reporting
 *     bucket, IRS recipient code. Stored values on in-flight persons are
 *     tolerated (RaoPerson slots + PeopleStep round-trip + CustInq hydration
 *     keep them intact); they simply no longer surface here.
 *   - "Foreign residence address" section REMOVED entirely — not captured in
 *     legacy procedure, not present on any typical captured document, never
 *     wired to extraction.
 *
 * Commit-pipeline follow-up (REPORT-ONLY, not built here): none of these
 * fields directly hit Core Director — at CIF creation the commit pipeline
 * (CartCommitService) should compose a CIF account Note from this section's
 * data. Source of truth at commit time: the person's canonical slots
 * (visaType / visaExpDt / i94Number / i94ExpDt / foreignPassportNumber /
 * foreignPassportCountry) plus person.extra.i94AdmitUntil and the per-doc
 * audit store person.extra.extracts.* — all of which ride the cart's
 * peopleJson into the commit payload already.
 */

import { useEffect, useMemo, useState } from 'react';
import type { AlienConfigEnvelope } from '@retail-ops/shared-types';
import type { RaoPerson } from '../../../types';
import { useApiClient } from '@/contexts/ApiClientContext';
import {
  detectCrossDocConflicts,
  type CapturedDocForConflict,
  type ConflictFinding,
} from '../capture/detectCrossDocConflicts';
import type { RetainedExtractField } from '../shared/nraScanPatch';

// Top-5 countries pinned per actual NRA population on file
// (reference_nra_cif_export_findings_20260427: 26% Mo S&T Indians/Chinese/etc.)
const TOP_COUNTRY_PINS: ReadonlyArray<string> = ['IN', 'CN', 'SA', 'PK', 'NG'];

const BLOCKED_VISAS_FALLBACK = new Set(['B-1', 'B-2', 'B1', 'B2', 'VWP', 'ESTA']);

/** Friendly labels for the per-doc extract store keys (NraDocumentType). */
const DOC_LABELS: Record<string, string> = {
  'passport': 'Foreign Passport',
  'visa': 'US Visa',
  'i94': 'I-94',
  'i20': 'I-20',
  'ds2019': 'DS-2019',
  'green-card': 'Permanent Resident Card',
  'ead': 'Employment Authorization',
  'primary-id': 'Photo ID',
};

type ExtractStore = Record<string, Record<string, RetainedExtractField>>;

/** Pull person.extra.extracts (per-doc analyzer results) defensively. */
function readExtractStore(person: RaoPerson): ExtractStore {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const raw = extra['extracts'];
  return raw && typeof raw === 'object' ? (raw as ExtractStore) : {};
}

/** First non-empty extract value for a doc type, across alternate keys. */
function extractValue(store: ExtractStore, docType: string, ...keys: string[]): string {
  const doc = store[docType];
  if (!doc || typeof doc !== 'object') return '';
  for (const k of keys) {
    const v = doc[k]?.value;
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** Only feed <input type="date"> values it can render. */
function asDateInputValue(v: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
}

/**
 * Build the cross-document comparison set from the analyzer extracts. Only
 * fields the comparator understands are mapped; documents with no comparable
 * values are skipped. ID-number cross-typing is intentionally NOT mapped
 * (different document types carry different numbers by nature — the B3
 * lesson); the foreign-passport number IS compared because the visa page and
 * the I-94 echo the SAME passport number, so a disagreement is a real signal.
 */
function buildNraConflictCaptures(store: ExtractStore): CapturedDocForConflict[] {
  const out: CapturedDocForConflict[] = [];
  for (const docType of Object.keys(store)) {
    const get = (...keys: string[]): string | null => {
      const v = extractValue(store, docType, ...keys);
      return v === '' ? null : v;
    };
    const cap: CapturedDocForConflict = {
      docId: docType,
      label: DOC_LABELS[docType] ?? docType,
      firstName: get('firstName'),
      middleName: get('middleName'),
      lastName: get('lastName'),
      dob: get('dateOfBirth', 'dob'),
      passportNumber: get('foreignPassportNumber', 'visaPassportNumberCrossRef'),
      gender: get('gender', 'sex'),
    };
    const hasComparable =
      cap.firstName || cap.lastName || cap.middleName || cap.dob || cap.passportNumber || cap.gender;
    if (hasComparable) out.push(cap);
  }
  return out;
}

const docLabel = (docId: string): string => DOC_LABELS[docId] ?? docId;

export interface IndividualNraSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged: (field: keyof RaoPerson) => boolean;
  readOnly?: boolean;
}

export function IndividualNraSection(
  props: IndividualNraSectionProps,
): JSX.Element | null {
  const { person, onChange, readOnly = false } = props;
  const client = useApiClient();
  const [cfg, setCfg] = useState<AlienConfigEnvelope | null>(null);
  // null = follow the auto rule (expand only on conflict); true/false = the
  // officer clicked the expander and their choice wins.
  const [manualExpand, setManualExpand] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    client.retail.config.alienConfig().then((res) => {
      if (alive && res?.success && res.data) setCfg(res.data);
    }).catch(() => {/* fall back to hardcoded lists */});
    return () => { alive = false; };
  }, [client]);

  // Cross-document conflict detection over the analyzer extract store —
  // drives the auto-expand rule and the in-section conflict callout.
  const extracts = useMemo(() => readExtractStore(person), [person]);
  const conflicts = useMemo<ReadonlyArray<ConflictFinding>>(
    () => detectCrossDocConflicts(buildNraConflictCaptures(extracts)),
    [extracts],
  );

  // Render only for Non-Resident Aliens. Permanent Residents do NOT get this
  // section (their SSN follows the standard path).
  if (person.citizenshipStatus !== 'Non-Resident Alien') return null;

  const blockedVisas = cfg
    ? new Set(cfg.visaTypes.filter((v) => !v.isEligible).map((v) => v.visaCode.toUpperCase()))
    : BLOCKED_VISAS_FALLBACK;

  // Consolidation (Bill 2026-06-11, LOCKED mockup v6) — person.visaType is now
  // populated ONLY by the captured visa stamp's extraction (normalizeVisaType)
  // or CustInq hydration; the typed visa-class select is gone (no Core
  // Director destination). The eligibility check fires from that extracted
  // class — live AlienConfig catalog when loaded, locked B-1/B-2/VWP/ESTA
  // fallback otherwise.
  const visaClass = (person.visaType ?? '') || extractValue(extracts, 'visa', 'visaType');
  const visaBlocked = visaClass ? blockedVisas.has(visaClass.trim().toUpperCase()) : false;

  // Display values — canonical person slot first, analyzer extract fallback.
  const visaExp = (person.visaExpDt ?? '') || extractValue(extracts, 'visa', 'visaExpDt');
  const i94Number = (person.i94Number ?? '') || extractValue(extracts, 'i94', 'i94Number');
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const i94AdmitUntil =
    (typeof extra['i94AdmitUntil'] === 'string' ? (extra['i94AdmitUntil'] as string) : '') ||
    extractValue(extracts, 'i94', 'i94AdmitUntil');
  // An I-94 admitted "D/S" (duration of status) has no fixed expiration date —
  // display the plain meaning, never a blank/null date.
  const i94IsDurationOfStatus =
    !person.i94ExpDt && i94AdmitUntil.trim().toUpperCase() === 'D/S';
  const i94Exp = (person.i94ExpDt ?? '') || asDateInputValue(i94AdmitUntil);
  const passportNumber =
    (person.foreignPassportNumber ?? '') ||
    extractValue(extracts, 'passport', 'foreignPassportNumber', 'pcbIdNumber');
  const passportCountry =
    (person.foreignPassportCountry ?? '') ||
    extractValue(extracts, 'passport', 'foreignPassportCountry', 'pcbIdIssuer');

  const expanded = manualExpand ?? conflicts.length > 0;

  // Compact one-line summary shown while collapsed.
  const summaryParts: string[] = [];
  if (visaClass) summaryParts.push(`Visa ${visaClass}${visaExp ? ` · expires ${visaExp}` : ''}`);
  if (i94Number) {
    summaryParts.push(
      `I-94 ${i94Number}${i94IsDurationOfStatus ? ' · D/S — duration of status' : i94Exp ? ` · expires ${i94Exp}` : ''}`,
    );
  }
  if (passportNumber) summaryParts.push(`Passport ${passportNumber}${passportCountry ? ` (${passportCountry})` : ''}`);
  const summaryLine = summaryParts.join('  ·  ');

  return (
    <section
      style={{
        margin: 'var(--sp-4) 0',
        padding: 'var(--sp-4)',
        border: '1px solid var(--ink-200, var(--surface-border))',
        borderRadius: 6,
        background: 'var(--surface-1, #fff)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--sp-2)' }}>
        <button
          type="button"
          onClick={() => setManualExpand(!expanded)}
          aria-expanded={expanded}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)' }}>
            {expanded ? '▾' : '▸'}
          </span>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
            Non-Resident Alien documentation
          </h3>
        </button>
        {conflicts.length > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--err-bg)',
              color: 'var(--err-text)',
              border: '1px solid var(--err-fill)',
            }}
          >
            {conflicts.length === 1 ? '1 conflict found' : `${conflicts.length} conflicts found`}
          </span>
        )}
      </header>

      <p style={{ margin: '0 0 var(--sp-3) 0', fontSize: 12, color: 'var(--ink-700, #4b5563)' }}>
        Summary of the customer&rsquo;s non-resident documentation — recorded to the
        customer file when the CIF is created.
      </p>

      {visaBlocked && (
        <div
          style={{
            marginBottom: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            border: '1px solid var(--err-fill)',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <strong>Visa not eligible for account opening.</strong> Per legacy procedure,
          B-1/B-2 tourist + VWP/ESTA visa-holders cannot open new accounts.
        </div>
      )}

      {!expanded && summaryLine && (
        <div style={{ fontSize: 12, color: 'var(--ink-700, #4b5563)' }}>{summaryLine}</div>
      )}

      {expanded && (
        <>
          {conflicts.length > 0 && (
            <div
              role="alert"
              style={{
                marginBottom: 'var(--sp-3)',
                padding: 'var(--sp-2) var(--sp-3)',
                background: 'var(--err-bg)',
                color: 'var(--err-text)',
                border: '1px solid var(--err-fill)',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <strong>The captured documents do not agree.</strong> Review the items
              below against the document images before continuing.
              <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                {conflicts.map((c) => (
                  <li key={c.ruleId} style={{ marginBottom: 2 }}>
                    {c.field}:{' '}
                    {c.values.map((v, i) => (
                      <span key={v.docId}>
                        {i > 0 ? ' vs ' : ''}
                        {docLabel(v.docId)} &ldquo;{v.value}&rdquo;
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Consolidation (Bill 2026-06-11) — the embedded DocumentTileGrid is
              REMOVED: the consolidated body's "Documents to capture" grid is the
              ONLY capture surface (tiles-only doctrine, 03-DETERMINISTIC-TILES).
              This section summarizes what the analyzer read off those captures. */}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: 'var(--sp-3)' }}>
            {/* NO visa-class input (Bill 2026-06-11) — the class is read from the
                captured visa stamp; this is a read-only echo of the extracted value. */}
            <Field label="Visa class (read from captured visa)">
              <input
                type="text"
                value={visaClass}
                readOnly
                disabled
                placeholder="— extracted from the US-Visa capture —"
                style={{ ...fieldStyle, background: 'var(--field-readonly-bg, #f5f5f5)', color: 'var(--ink-700, #4b5563)' }}
                title="Read from the captured visa — not typed"
              />
            </Field>

            <Field label="Visa expiration">
              <input
                type="date"
                value={asDateInputValue(visaExp)}
                onChange={(e) => onChange({ visaExpDt: e.target.value })}
                disabled={readOnly}
                style={fieldStyle}
              />
            </Field>

            <Field label="I-94 admission number">
              <input
                type="text"
                value={i94Number}
                onChange={(e) => onChange({ i94Number: e.target.value })}
                disabled={readOnly}
                placeholder="11-digit"
                style={fieldStyle}
              />
            </Field>

            <Field label="I-94 expiration">
              {i94IsDurationOfStatus ? (
                <input
                  type="text"
                  value="D/S — duration of status"
                  readOnly
                  disabled
                  style={{ ...fieldStyle, background: 'var(--field-readonly-bg, #f5f5f5)', color: 'var(--ink-700, #4b5563)' }}
                  title="Admitted for duration of status — no fixed expiration date"
                />
              ) : (
                <input
                  type="date"
                  value={asDateInputValue(i94Exp)}
                  onChange={(e) => onChange({ i94ExpDt: e.target.value })}
                  disabled={readOnly}
                  style={fieldStyle}
                />
              )}
            </Field>

            <Field label="Foreign passport number">
              <input
                type="text"
                value={passportNumber}
                // NRA smoke test (Bill 2026-06-12): do NOT mirror the passport
                // number into fornTaxId — a passport does not carry a foreign
                // tax ID. The scan-path equivalents (captureRouting /
                // nraScanPatch) were fixed by the capture lane; this was the
                // last manual-entry instance.
                onChange={(e) => onChange({ foreignPassportNumber: e.target.value })}
                disabled={readOnly}
                style={fieldStyle}
              />
            </Field>

            <Field label="Passport issuing country">
              <CountrySelect
                value={passportCountry}
                onChange={(v) => onChange({ foreignPassportCountry: v })}
                disabled={readOnly}
                cfg={cfg}
              />
            </Field>
          </div>

          {/* W-8 fields (country of tax residency, W-8 form type / last signed /
              expiration, IRS reporting bucket, IRS recipient code) REMOVED —
              W-8 is not required for CIP (Bill, NRA smoke test 2026-06-12).
              Stored values on in-flight persons remain tolerated in RaoPerson /
              peopleJson round-trip; they just no longer surface here.

              "Foreign residence address" section REMOVED — not captured in
              legacy procedure, not on any typical captured document, never
              wired to extraction. */}
        </>
      )}
    </section>
  );
}

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--ink-300, #d1d5db)',
  borderRadius: 4,
  fontSize: 13,
};

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, gridColumn: wide ? 'span 2' : undefined }}>
      <span style={{ fontWeight: 500, color: 'var(--ink-800, #1f2937)' }}>{label}</span>
      {children}
    </label>
  );
}

/**
 * Country picker. When the live AlienConfig is available, options come from
 * dbo.TreatyCountries (admin-config'd 20-country list with treaty article +
 * claimed rate). Top-5 PCB-NRA-population countries (India / China / Saudi /
 * Pakistan / Nigeria) are pinned in a "Common at PCB" optgroup. Falls back
 * to the hardcoded list pre-fetch.
 */
function CountrySelect(props: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  cfg: AlienConfigEnvelope | null;
}): JSX.Element {
  const live = props.cfg?.treatyCountries ?? null;
  const liveCommon = live ? live.filter((c) => TOP_COUNTRY_PINS.includes(c.countryIso2)) : null;
  const liveOther = live ? live.filter((c) => !TOP_COUNTRY_PINS.includes(c.countryIso2)) : null;

  const fallbackRest = [
    'AU', 'BR', 'CA', 'EG', 'FR', 'DE', 'IL', 'IT', 'JP', 'KE',
    'KR', 'KW', 'MX', 'NP', 'NL', 'NZ', 'PH', 'PL', 'PT', 'QA',
    'RU', 'SG', 'ZA', 'ES', 'SE', 'CH', 'TW', 'TH', 'TR', 'AE',
    'GB', 'UA', 'VN',
  ];

  return (
    <select value={props.value} onChange={(e) => props.onChange(e.target.value)} disabled={props.disabled} style={fieldStyle}>
      <option value="">(select)</option>
      {live ? (
        <>
          <optgroup label="Common at PCB">
            {liveCommon!.map((c) => (
              <option key={c.countryIso2} value={c.countryIso2}>
                {c.countryIso2} — {c.displayName}
              </option>
            ))}
          </optgroup>
          <optgroup label="Other">
            {liveOther!.map((c) => (
              <option key={c.countryIso2} value={c.countryIso2}>
                {c.countryIso2} — {c.displayName}
              </option>
            ))}
          </optgroup>
        </>
      ) : (
        <>
          <optgroup label="Common at PCB">
            {TOP_COUNTRY_PINS.map((c) => <option key={c} value={c}>{c} — {countryName(c)}</option>)}
          </optgroup>
          <optgroup label="Other">
            {fallbackRest.sort().map((c) => <option key={c} value={c}>{c} — {countryName(c)}</option>)}
          </optgroup>
        </>
      )}
    </select>
  );
}

function countryName(iso: string): string {
  const m: Record<string, string> = {
    IN: 'India', CN: 'China', SA: 'Saudi Arabia', PK: 'Pakistan', NG: 'Nigeria',
    AU: 'Australia', BR: 'Brazil', CA: 'Canada', EG: 'Egypt', FR: 'France',
    DE: 'Germany', IL: 'Israel', IT: 'Italy', JP: 'Japan', KE: 'Kenya',
    KR: 'South Korea', KW: 'Kuwait', MX: 'Mexico', NP: 'Nepal', NL: 'Netherlands',
    NZ: 'New Zealand', PH: 'Philippines', PL: 'Poland', PT: 'Portugal', QA: 'Qatar',
    RU: 'Russia', SG: 'Singapore', ZA: 'South Africa', ES: 'Spain', SE: 'Sweden',
    CH: 'Switzerland', TW: 'Taiwan', TH: 'Thailand', TR: 'Turkey', AE: 'UAE',
    GB: 'United Kingdom', UA: 'Ukraine', VN: 'Vietnam',
  };
  return m[iso] ?? iso;
}
